/* eslint-disable import/prefer-default-export */
import { SearchState } from '@customTypes/search';
import {
  GetAppliedFiltersCount,
  PoolFeatures,
  PoolProductTypes,
  SpaFeatures,
  SpaProductTypes,
  AmenitiesFriendlyNames,
  GetAmenitiesToShow,
  HotelStarsFriendlyNames,
} from '@context/SearchContext2025';
import { GetCanonicalURL } from '@helpers/environment';
import { GenerateSearchUrl } from '@helpers/url';

/*
 * Generates a canonical URL for the SRP (Search Results Page) based on the provided SearchState.
 * This function is used to create a consistent URL format for search results.
 *
 * @param s - The SearchState object containing search parameters.
 * @returns string - The canonical URL for the SRP.
 */
export function getSRPCanonicalURL(s: SearchState): string {
  // Generate the search URL using the provided SearchState
  const searchPath = GenerateSearchUrl(s);
  const searchUrl = new URL(searchPath, 'http://localhost'); // Use a dummy base URL for URL parsing
  const rootCanonicalURL = GetCanonicalURL(searchPath);
  // NOTE: Future iteration will have a priority map of amenities and filters we want to
  // allow for indexation. But the first version, we are only going to allow indexation
  // of the first query parameter.

  // Count how many filter are applied
  if (GetAppliedFiltersCount(s) === 1) {
    const searchParam = searchUrl.search.includes('&')
      ? searchUrl.search.split('&')[0]
      : searchUrl.search;
    return `${rootCanonicalURL}${searchParam}`;
  }
  return rootCanonicalURL;
}

/**
 * This function is used to generate the title for the Search Results Page (SRP).
 *
 * @param searchState - The SearchState object containing search parameters.
 * @return string - The title for the SRP.
 */
export function getSRPTitle(search: SearchState, location: string): string {
  const { product_category: productCategory } = search;

  // Here we go... big matrix of titles for single filter pages.
  // https://docs.google.com/spreadsheets/d/1Lx5lrHi8VjNkrzpd8OnWOLSTnY3Z75L2HR6-sZ0_yiw/edit?gid=0#gid=0
  if (GetAppliedFiltersCount(search) === 1 && location) {
    // If only one filter is applied, we use a title that is specific to that filter

    const getPoolFeaturesTitle = (searchState: SearchState): string | null => {
      const category = searchState.product_category;
      if (category !== '' && category !== 'pool' && category !== 'day room') return null;
      const feature = PoolFeatures.find(
        (key) => searchState.filters.amenities[key as keyof SearchState['filters']['amenities']],
      );

      if (!feature) return null;
      const prefix =
        category === 'day room' ? 'Best Day Rooms at Hotels with' : 'Day Pass to the Best';
      const suffix = `in ${location} | ResortPass`;
      switch (feature) {
        case 'hottub':
          return category === 'day room'
            ? [prefix, 'Hot Tubs', suffix].join(' ')
            : [prefix, 'Hotel Hot Tubs', suffix].join(' ');
        case 'outdoor-pool':
          return [prefix, 'Outdoor Pools', suffix].join(' ');
        case 'rooftop-pool':
          return [prefix, 'Rooftop Pools', suffix].join(' ');
        case 'infinity-pool':
          return [prefix, 'Infinity Pools', suffix].join(' ');
        case 'indoor-pool':
          return [prefix, 'Indoor Pools', suffix].join(' ');
        case 'kiddie-pool':
          return category === 'day room'
            ? [prefix, 'Kiddie Pools', suffix].join(' ')
            : [prefix, 'Hotel Kiddie Pools', suffix].join(' ');
        case 'waterpark':
          return [prefix, 'Waterparks', suffix].join(' ');
        case 'splash-pad':
          return category === 'day room'
            ? [prefix, 'Pool Splash Pads', suffix].join(' ')
            : [prefix, 'Hotel Pool Splash Pads', suffix].join(' ');
        case 'lazyriver':
          return [prefix, 'Lazy Rivers', suffix].join(' ');
        case 'waterslide':
          return category === 'day room'
            ? [prefix, 'Pool Waterslides', suffix].join(' ')
            : [prefix, 'Hotel Pool Waterslides', suffix].join(' ');
        default:
          return null;
      }
    };

    const getPoolProductType = (searchState: SearchState): string | null => {
      const category = searchState.product_category;
      if (category !== '' && category !== 'pool' && category !== 'day room') return null;
      const productType = Object.entries(PoolProductTypes).find(([, id]) =>
        searchState.filters.product_types.includes(id));
      if (!productType) return null;
      const prefix =
        category === 'day room' ? 'Best Day Rooms at Hotels with' : 'Day Pass to the Best';
      const suffix = `in ${location} | ResortPass`;
      switch (productType[1]) {
        case 1: // Cabana
          return category === 'day room'
            ? [prefix, 'Cabanas', suffix].join(' ')
            : [prefix, 'Pool & Beach Cabanas', suffix].join(' ');
        case 4: // Day Bed
          return category === 'day room'
            ? [prefix, 'Daybeds', suffix].join(' ')
            : [prefix, 'Pool & Beach Daybeds', suffix].join(' ');
        default:
          return null;
      }
    };

    const getSpaFeaturesTitle = (searchState: SearchState): string | null => {
      const category = searchState.product_category;
      if (category !== 'spa') return null;
      const feature = SpaFeatures.find(
        (key) => searchState.filters.amenities[key as keyof SearchState['filters']['amenities']],
      );
      if (!feature) return null;
      const suffix = `in ${location} | ResortPass`;
      switch (feature) {
        case 'sauna':
          return ['Best Spa Saunas', suffix].join(' ');
        case 'steam-room':
          return ['Best Spa Steam Rooms', suffix].join(' ');
        case 'cold-plunge':
          return ['Best Spa Cold Plunges', suffix].join(' ');
        case 'infrared-sauna':
          return ['Best Spa Infrared Saunas', suffix].join(' ');
        case 'salt-room':
          return ['Best Spa Salt Rooms', suffix].join(' ');
        case 'thermal-pool':
          return ['Best Spa Thermal Pools', suffix].join(' ');
        case 'relaxation-lounge':
          return ['Best Spa Relaxation Lounges', suffix].join(' ');
        case 'led-and-red-light-therapy':
          return ['Best Spa LED and Red Light Therapy', suffix].join(' ');
        default:
          return null;
      }
    };

    const getSpaServicesTitle = (searchState: SearchState): string | null => {
      const category = searchState.product_category;
      if (category !== 'spa') return null;
      const service = Object.entries(SpaProductTypes).find(([, id]) =>
        searchState.filters.product_types.includes(id));
      if (!service) return null;
      const prefix = 'Best Spa';
      const suffix = `in ${location} | ResortPass`;
      switch (service[1]) {
        case 3:
          return [prefix, 'Passes', suffix].join(' ');
        case 7:
          return [prefix, 'Massages', suffix].join(' ');
        case 50:
          return [prefix, 'Body Treatments', suffix].join(' ');
        case 53:
          return [prefix, 'Couples Treatments', suffix].join(' ');
        case 51:
          return [prefix, 'Facials', suffix].join(' ');
        case 52:
          return [prefix, 'Nail Services', suffix].join(' ');
        default:
          return null;
      }
    };

    const getAmenitiesTitle = (searchState: SearchState): string | null => {
      const category = searchState.product_category;
      const amenities = GetAmenitiesToShow(category);
      const appliedAmenity = amenities.find((a) => searchState.filters.amenities[a]);
      if (!appliedAmenity) return null;

      const prefix = (() => {
        if (category === 'day room') return 'Best Day Rooms at Hotels with';
        if (category === 'spa') return 'Best Spas with';
        return 'Day Pass to the Best Hotels with';
      })();

      // Alternative prefix for some amenities
      const prefixAlt = (() => {
        if (category === 'day room') return 'Best Day Rooms at';
        if (category === 'spa') return 'Best Spas with';
        return 'Day Pass to the Best';
      })();
      const suffix = `in ${location} | ResortPass`;

      switch (appliedAmenity) {
        case 'all-inclusive':
          return [prefixAlt, 'All-Inclusive Hotels', suffix].join(' ');
        case 'beach':
          return [prefixAlt, 'Beach Access Hotels', suffix].join(' ');
        case 'wheelchair':
          return [prefixAlt, 'Wheelchair Accessible Hotels', suffix].join(' ');
        default:
          return [prefix, AmenitiesFriendlyNames[appliedAmenity], suffix].join(' ');
      }
    };

    const getHotelStarsTitle = (searchState: SearchState): string | null => {
      const star = Object.keys(searchState.filters.hotel_stars).find(
        (key) =>
          searchState.filters.hotel_stars[key as keyof SearchState['filters']['hotel_stars']],
      );
      if (!star) return null;
      const category = searchState.product_category;
      const prefix = (() => {
        if (category === 'day room') return 'Best Day Rooms at';
        if (category === 'spa') return 'Best';
        return 'Day Pass to the Best';
      })();
      const suffix = (() => {
        if (category === 'pool') return `Pools in ${location} | ResortPass`;
        if (category === 'spa') return `Spas in ${location} | ResortPass`;
        return `Hotels in ${location} | ResortPass`;
      })();
      return [
        prefix,
        HotelStarsFriendlyNames[star as keyof SearchState['filters']['hotel_stars']],
        suffix,
      ].join(' ');
    };

    const getGuestRatingTitle = (searchState: SearchState): string | null => {
      const rating = Object.entries(searchState.filters.guest_ratings).find(([, value]) => value);

      if (!rating) return null;
      const category = searchState.product_category;
      switch (category) {
        case 'pool':
          return `Day Pass to the Top Rated Pools in ${location} | ResortPass`;
        case 'spa':
          return `Top Rated Spas in ${location} | ResortPass`;
        case 'day room':
          return `Best Day Rooms at Top Rated Hotels in ${location} | ResortPass`;
        default:
          return `Day Pass to the Top Rated Hotels in ${location} | ResortPass`;
      }
    };

    const getVibesTitle = (searchState: SearchState): string | null => {
      const vibe = Object.entries(searchState.filters.vibes).find(([, value]) => value);
      if (!vibe) return null;
      const category = searchState.product_category;
      if (category === 'day room') {
        switch (vibe[0]) {
          case 'family-friendly':
            return `Best Family Friendly Hotel Day Rooms in ${location} | ResortPass`;
          case 'party':
            return `Best Hotel Day Rooms at Party Vibe Hotels in ${location} | ResortPass`;
          case 'serene':
            return `Best Serene Hotel Day Rooms in ${location} | ResortPass`;
          case 'luxe':
            return `Best Luxe Hotel Day Rooms in ${location} | ResortPass`;
          case 'trendy':
            return `Best Trendy Hotel Day Rooms in ${location} | ResortPass`;
          default:
            return `Best ${
              vibe[0].charAt(0).toUpperCase() + vibe[0].slice(1)
            } Hotel Day Rooms in ${location} | ResortPass`;
        }
      }

      switch (vibe[0]) {
        case 'family-friendly':
          return `Best Pool Day Passes for Families and Kids in ${location} | ResortPass`;
        case 'party':
          return `Best Party Pools in ${location} | ResortPass`;
        case 'serene':
          return `Best Serene Pools in ${location} | ResortPass`;
        case 'luxe':
          return `Best Luxe Pools in ${location} | ResortPass`;
        case 'trendy':
          return `Best Trendy Pools in ${location} | ResortPass`;
        default:
          return `Best ${
            vibe[0].charAt(0).toUpperCase() + vibe[0].slice(1)
          } Pools in ${location} | ResortPass`;
      }
    };

    const title = [
      getPoolFeaturesTitle(search),
      getPoolProductType(search),
      getSpaFeaturesTitle(search),
      getSpaServicesTitle(search),
      getAmenitiesTitle(search),
      getHotelStarsTitle(search),
      getGuestRatingTitle(search),
      getVibesTitle(search),
    ].find((t) => t !== null);

    if (title) return title;
  }
  // the default titles
  const defaultSuffix = location ? `in ${location} | ResortPass` : '| ResortPass';
  switch (productCategory) {
    case 'pool':
      return `Best Hotel Pool Passes and Cabanas ${defaultSuffix}`;
    case 'spa':
      return `Best Spa Passes and Treatments ${defaultSuffix}`;
    case 'day room':
      return `Best Day Use Rooms ${defaultSuffix}`;
    default:
      return `Best Hotel Pool Day Passes ${defaultSuffix}`;
  }
}

/**
 * This function is used to generate the H1 heading for the Search Results Page (SRP).
 * Based on the matrix.csv file that defines the heading text for single filter scenarios.
 *
 * @param searchState - The SearchState object containing search parameters.
 * @param location - The location string to be inserted into the heading.
 * @return string - The H1 heading for the SRP.
 */
export function getSRPH1(search: SearchState, location: string): string {
  const { product_category: productCategory } = search;

  // Single filter H1 headings based on matrix.csv
  if (GetAppliedFiltersCount(search) === 1 && location) {
    const getPoolFeaturesH1 = (searchState: SearchState): string | null => {
      const category = searchState.product_category;
      if (category !== '' && category !== 'pool' && category !== 'day room') return null;
      const feature = PoolFeatures.find(
        (key) => searchState.filters.amenities[key as keyof SearchState['filters']['amenities']],
      );

      if (!feature) return null;
      const locationText = `in and near ${location}`;

      switch (feature) {
        case 'hottub':
          return category === 'day room'
            ? `Day Use Rooms at Hotels with Hot Tubs ${locationText}`
            : `Hot Tubs ${locationText}`;
        case 'outdoor-pool':
          return category === 'day room'
            ? `Day Use Rooms at Hotels with Outdoor Pools ${locationText}`
            : `Outdoor Pools ${locationText}`;
        case 'rooftop-pool':
          return category === 'day room'
            ? `Day Use Rooms at Hotels with Rooftop Pools ${locationText}`
            : `Rooftop Pools ${locationText}`;
        case 'infinity-pool':
          return category === 'day room'
            ? `Day Use Rooms at Hotels with Infinity Pools ${locationText}`
            : `Infinity Pools ${locationText}`;
        case 'indoor-pool':
          return category === 'day room'
            ? `Day Use Rooms at Hotels with Indoor Pools ${locationText}`
            : `Indoor Pools ${locationText}`;
        case 'kiddie-pool':
          return category === 'day room'
            ? `Day Use Rooms at Hotels with Kiddie Pools ${locationText}`
            : `Kiddie Pools ${locationText}`;
        case 'waterpark':
          return category === 'day room'
            ? `Day Use Rooms at Hotels with Waterparks ${locationText}`
            : `Waterparks ${locationText}`;
        case 'splash-pad':
          return category === 'day room'
            ? `Day Use Rooms at Hotels with Splash Pads ${locationText}`
            : `Splash Pads ${locationText}`;
        case 'lazyriver':
          return category === 'day room'
            ? `Day Use Rooms at Hotels with Lazy Rivers ${locationText}`
            : `Lazy Rivers ${locationText}`;
        case 'waterslide':
          return category === 'day room'
            ? `Day Use Rooms at Hotels with Waterslides ${locationText}`
            : `Waterslides ${locationText}`;
        default:
          return null;
      }
    };

    const getPoolProductTypeH1 = (searchState: SearchState): string | null => {
      const category = searchState.product_category;
      if (category !== '' && category !== 'pool' && category !== 'day room') return null;
      const productType = Object.entries(PoolProductTypes).find(([, id]) =>
        searchState.filters.product_types.includes(id));
      if (!productType) return null;
      const locationText = `in and near ${location}`;

      switch (productType[1]) {
        case 1: // Cabana
          return category === 'day room'
            ? `Day Use Rooms at Hotels with Cabanas ${locationText}`
            : `Pool & Beach Cabanas ${locationText}`;
        case 4: // Day Bed
          return category === 'day room'
            ? `Day Use Rooms at Hotels with Daybeds ${locationText}`
            : `Pool & Beach Daybeds ${locationText}`;
        default:
          return null;
      }
    };

    const getSpaFeaturesH1 = (searchState: SearchState): string | null => {
      const category = searchState.product_category;
      if (category !== 'spa') return null;
      const feature = SpaFeatures.find(
        (key) => searchState.filters.amenities[key as keyof SearchState['filters']['amenities']],
      );
      if (!feature) return null;
      const locationText = `in and near ${location}`;

      switch (feature) {
        case 'sauna':
          return `Saunas ${locationText}`;
        case 'steam-room':
          return `Steam Rooms ${locationText}`;
        case 'cold-plunge':
          return `Cold Plunges ${locationText}`;
        case 'infrared-sauna':
          return `Infrared Saunas ${locationText}`;
        case 'salt-room':
          return `Salt Rooms ${locationText}`;
        case 'thermal-pool':
          return `Thermal Pools ${locationText}`;
        case 'relaxation-lounge':
          return `Relaxation Lounges ${locationText}`;
        case 'led-and-red-light-therapy':
          return `LED/Red Light Therapy ${locationText}`;
        default:
          return null;
      }
    };

    const getSpaServicesH1 = (searchState: SearchState): string | null => {
      const category = searchState.product_category;
      if (category !== 'spa') return null;
      const service = Object.entries(SpaProductTypes).find(([, id]) =>
        searchState.filters.product_types.includes(id));
      if (!service) return null;
      const locationText = `in and near ${location}`;

      switch (service[1]) {
        case 3:
          return `Spa Passes ${locationText}`;
        case 7:
          return `Massages ${locationText}`;
        case 50:
          return `Body Treatments ${locationText}`;
        case 53:
          return `Couples Treatments ${locationText}`;
        case 51:
          return `Facials ${locationText}`;
        case 52:
          return `Nail Services ${locationText}`;
        default:
          return null;
      }
    };

    const getAmenitiesH1 = (searchState: SearchState): string | null => {
      const category = searchState.product_category;
      const amenities = GetAmenitiesToShow(category);
      const appliedAmenity = amenities.find((a) => searchState.filters.amenities[a]);
      if (!appliedAmenity) return null;

      const locationText = `in and near ${location}`;

      switch (appliedAmenity) {
        case 'beach':
          if (category === 'spa') return null; // No spa beach access in matrix
          if (category === 'day room') {
            return `Day Use Rooms with Beach Access ${locationText}`;
          }
          if (category === 'pool') {
            return `Pools with Beach Access ${locationText}`;
          }
          return `Hotel Day Passes with Beach Access ${locationText}`;
        case 'all-inclusive':
          if (category === 'spa') return null; // No spa all-inclusive in matrix
          if (category === 'day room') {
            return `Day Use Rooms at All-Inclusive Hotels ${locationText}`;
          }
          if (category === 'pool') {
            return `All-Inclusive Pools ${locationText}`;
          }
          return `All-Inclusive Hotel Day Passes ${locationText}`;
        case 'free-parking':
          if (category === 'day room') {
            return `Day Use Rooms with Free Parking ${locationText}`;
          }
          if (category === 'pool') {
            return `Pools with Free Parking ${locationText}`;
          }
          if (category === 'spa') {
            return `Spas with Free Parking ${locationText}`;
          }
          return `Hotel Day Passes with Free Parking ${locationText}`;
        case 'luggage-storage':
          if (category === 'day room') {
            return `Day Use Rooms with Luggage Storage ${locationText}`;
          }
          if (category === 'pool') {
            return `Pools with Luggage Storage ${locationText}`;
          }
          if (category === 'spa') {
            return `Spas with Luggage Storage ${locationText}`;
          }
          return `Hotel Day Passes with Luggage Storage ${locationText}`;
        case 'airport-shuttle':
          if (category === 'day room') {
            return `Day Use Rooms with Airport Shuttles ${locationText}`;
          }
          if (category === 'pool') {
            return `Pools with Airport Shuttle ${locationText}`;
          }
          if (category === 'spa') {
            return `Spas with Airport Shuttle ${locationText}`;
          }
          return `Hotel Day Passes with Airport Shuttle ${locationText}`;
        case 'cruise-shuttle':
          if (category === 'day room') {
            return `Day Use Rooms with Cruise Port Shuttles ${locationText}`;
          }
          if (category === 'pool') {
            return `Pools with Cruise Port Shuttle ${locationText}`;
          }
          if (category === 'spa') {
            return `Spas with Cruise Port Shuttle ${locationText}`;
          }
          return `Hotel Day Passes with Cruise Port Shuttle ${locationText}`;
        case 'fitness-center':
          if (category === 'day room') {
            return `Day Use Rooms with Gyms ${locationText}`;
          }
          if (category === 'pool') {
            return `Pools with Gym Access ${locationText}`;
          }
          if (category === 'spa') {
            return `Spas with Gym Access ${locationText}`;
          }
          return `Hotel Day Passes with Gym Access ${locationText}`;
        case 'wheelchair':
          if (category === 'spa') return null; // No spa wheelchair access in matrix
          if (category === 'day room') {
            return `Wheelchair Accessible Day Use Rooms ${locationText}`;
          }
          if (category === 'pool') {
            return `Wheelchair Accessible Pools ${locationText}`;
          }
          return `Wheelchair Accessible Hotel Day Passes ${locationText}`;
        case 'showers':
          if (category === 'spa') return null; // No spa showers in matrix
          if (category === 'day room') {
            return `Day Use Rooms with Showers ${locationText}`;
          }
          if (category === 'pool') {
            return `Pools with Showers ${locationText}`;
          }
          return `Hotel Day Passes with Showers ${locationText}`;
        case 'lockers':
          if (category === 'spa') return null; // No spa lockers in matrix
          if (category === 'day room') {
            return `Day Use Rooms with Locker Access ${locationText}`;
          }
          if (category === 'pool') {
            return `Pools with Lockers ${locationText}`;
          }
          return `Hotel Day Passes with Lockers ${locationText}`;
        default:
          return null;
      }
    };

    const getHotelStarsH1 = (searchState: SearchState): string | null => {
      const star = Object.keys(searchState.filters.hotel_stars).find(
        (key) =>
          searchState.filters.hotel_stars[key as keyof SearchState['filters']['hotel_stars']],
      );
      if (!star) return null;
      const category = searchState.product_category;
      const locationText = `in and near ${location}`;

      const starText = (() => {
        switch (star) {
          case '=5':
            return '5-Star';
          case '=4':
            return '4-Star';
          case '=3':
            return '3-Star';
          default:
            return null;
        }
      })();

      if (!starText) return null;

      switch (category) {
        case 'day room':
          return `${starText} Day Use Rooms ${locationText}`;
        case 'pool':
          return `${starText} Pools ${locationText}`;
        case 'spa':
          return `${starText} Spas ${locationText}`;
        default:
          return `${starText} Hotel Day Passes ${locationText}`;
      }
    };

    const getGuestRatingH1 = (searchState: SearchState): string | null => {
      const rating = Object.entries(searchState.filters.guest_ratings).find(([, value]) => value);
      if (!rating) return null;

      const category = searchState.product_category;
      const locationText = `in and near ${location}`;

      switch (category) {
        case 'day room':
          return `Top Rated Day Use Rooms ${locationText}`;
        case 'pool':
          return `Top Rated Pools ${locationText}`;
        case 'spa':
          return `Top Rated Spas ${locationText}`;
        default:
          return `Top Rated Hotel Day Passes ${locationText}`;
      }
    };

    const getVibesH1 = (searchState: SearchState): string | null => {
      const vibe = Object.entries(searchState.filters.vibes).find(([, value]) => value);
      if (!vibe) return null;

      const category = searchState.product_category;
      const locationText = `in and near ${location}`;
      const vibeText = vibe[0].charAt(0).toUpperCase() + vibe[0].slice(1).replace('-', ' ');

      if (category === 'spa') return null; // No spa vibes in matrix

      switch (category) {
        case 'day room':
          return `${vibeText} Day Use Rooms ${locationText}`;
        case 'pool':
          return `${vibeText} Pools ${locationText}`;
        default:
          return `${vibeText} Hotel Day Passes ${locationText}`;
      }
    };

    const h1 = [
      getPoolFeaturesH1(search),
      getPoolProductTypeH1(search),
      getSpaFeaturesH1(search),
      getSpaServicesH1(search),
      getAmenitiesH1(search),
      getHotelStarsH1(search),
      getGuestRatingH1(search),
      getVibesH1(search),
    ].find((h) => h !== null);

    if (h1) return h1;
  }

  // Default H1 headings for multiple filters or no specific single filter
  const defaultLocationText = location ? ` in and near ${location}` : '';
  switch (productCategory) {
    case 'pool':
      return `Pools${defaultLocationText}`;
    case 'spa':
      return `Spas${defaultLocationText}`;
    case 'day room':
      return `Day Use Hotel Rooms${defaultLocationText}`;
    default:
      return `Hotel Day Passes${defaultLocationText}`;
  }
}
